/**
 * Personalakte-Tab „Kontaktdaten": Rufnummern, E-Mail-Adressen (mit
 * „Als Standard setzen", G-2) und Adressen — drei SubTableEditor-Bereiche.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Star } from "lucide-react"
import { toast } from "sonner"
import type { FieldDef, FormValues } from "@/components/admin/EntityFormSheet"
import type { Column } from "@/components/admin/MasterDataSection"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { api, apiError } from "@/services/api"
import type {
  EmailLabel,
  EmployeeAddress,
  EmployeeAddressCreate,
  EmployeeEmail,
  EmployeeEmailCreate,
  PhoneNumber,
  PhoneNumberCreate,
  RufnummerLabel,
  WohnsitzArt,
} from "@/types"
import { EMAIL_LABEL, RUFNUMMER_LABEL, WOHNSITZ_LABEL, toOptions } from "./labels"
import { SubTableEditor } from "./SubTableEditor"

const str = (values: FormValues, key: string): string =>
  typeof values[key] === "string" ? (values[key] as string).trim() : ""

// ---------------------------------------------------------------------------
// Rufnummern
// ---------------------------------------------------------------------------

const PHONE_COLUMNS: Column<PhoneNumber>[] = [
  { header: "Label", cell: (r) => RUFNUMMER_LABEL[r.label] },
  { header: "Rufnummer", cell: (r) => <span className="font-medium">{r.phone}</span> },
  {
    header: "Freigegeben",
    cell: (r) =>
      r.released ? <Badge variant="secondary">freigegeben</Badge> : <span className="text-muted-foreground">—</span>,
  },
]

const PHONE_FIELDS: FieldDef[] = [
  { key: "label", label: "Label", kind: "select", required: true, options: toOptions(RUFNUMMER_LABEL) },
  { key: "phone", label: "Rufnummer", kind: "text", required: true },
  { key: "released", label: "Freigegeben", kind: "switch" },
]

// ---------------------------------------------------------------------------
// E-Mail-Adressen
// ---------------------------------------------------------------------------

const EMAIL_FIELDS: FieldDef[] = [
  { key: "label", label: "Label", kind: "select", required: true, options: toOptions(EMAIL_LABEL) },
  { key: "email", label: "E-Mail-Adresse", kind: "text", required: true },
]

// ---------------------------------------------------------------------------
// Adressen
// ---------------------------------------------------------------------------

const ADDRESS_COLUMNS: Column<EmployeeAddress>[] = [
  {
    header: "Anschrift",
    cell: (r) => (
      <span className="font-medium">
        {r.street} {r.houseNumber ?? ""}
      </span>
    ),
  },
  { header: "PLZ / Ort", cell: (r) => `${r.postalCode} ${r.city}` },
  { header: "Wohnsitz", cell: (r) => WOHNSITZ_LABEL[r.residenceType] },
  { header: "Notiz", cell: (r) => r.note ?? "—" },
]

const ADDRESS_FIELDS: FieldDef[] = [
  { key: "street", label: "Straße", kind: "text", required: true },
  { key: "houseNumber", label: "Hausnummer", kind: "text" },
  { key: "postalCode", label: "PLZ", kind: "text", required: true },
  { key: "city", label: "Ort", kind: "text", required: true },
  {
    key: "residenceType",
    label: "Wohnsitz-Art",
    kind: "select",
    required: true,
    options: toOptions(WOHNSITZ_LABEL),
  },
  { key: "note", label: "Notiz", kind: "text" },
]

export function ContactTab({ employeeId, canWrite }: { employeeId: string; canWrite: boolean }) {
  const queryClient = useQueryClient()

  const setPrimary = useMutation({
    mutationFn: (emailId: string) => api.employees.setPrimaryEmail(employeeId, emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee", employeeId] })
      toast.success("Standard-Adresse gesetzt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const emailColumns: Column<EmployeeEmail>[] = [
    { header: "Label", cell: (r) => EMAIL_LABEL[r.label] },
    { header: "E-Mail-Adresse", cell: (r) => <span className="font-medium">{r.email}</span> },
    {
      header: "Standard",
      cell: (r) =>
        r.released ? (
          <Badge className="bg-green-100 text-green-800 border-green-300" variant="outline">
            Standard
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ]

  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h3 className="text-base font-semibold">Rufnummern</h3>
        <SubTableEditor<PhoneNumber, PhoneNumberCreate>
          employeeId={employeeId}
          queryKey="phone-numbers"
          crud={api.employees.phoneNumbers}
          columns={PHONE_COLUMNS}
          fields={PHONE_FIELDS}
          entityLabel="Rufnummer"
          createLabel="Neue Rufnummer"
          emptyText="Keine Rufnummern hinterlegt."
          canWrite={canWrite}
          toValues={(row) => ({
            label: row?.label ?? "MOBIL",
            phone: row?.phone ?? "",
            released: row?.released ?? false,
          })}
          fromValues={(values) => ({
            label: str(values, "label") as RufnummerLabel,
            phone: str(values, "phone"),
            released: values.released === true,
          })}
        />
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold">E-Mail-Adressen</h3>
        <p className="text-sm text-muted-foreground">
          Genau eine Adresse ist der freigegebene Standard (G-2) — Aktion „Als Standard setzen".
        </p>
        <SubTableEditor<EmployeeEmail, EmployeeEmailCreate>
          employeeId={employeeId}
          queryKey="emails"
          crud={api.employees.emails}
          columns={emailColumns}
          fields={EMAIL_FIELDS}
          entityLabel="E-Mail-Adresse"
          createLabel="Neue E-Mail-Adresse"
          emptyText="Keine E-Mail-Adressen hinterlegt."
          canWrite={canWrite}
          toValues={(row) => ({
            label: row?.label ?? "GESCHAEFTLICH",
            email: row?.email ?? "",
          })}
          fromValues={(values) => ({
            label: str(values, "label") as EmailLabel,
            email: str(values, "email"),
          })}
          rowActions={(row) =>
            canWrite && !row.released ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="Als Standard setzen"
                title="Als Standard setzen"
                disabled={setPrimary.isPending}
                onClick={() => setPrimary.mutate(row.id)}
              >
                <Star className="!size-3.5" />
              </Button>
            ) : null
          }
        />
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold">Adressen</h3>
        <SubTableEditor<EmployeeAddress, EmployeeAddressCreate>
          employeeId={employeeId}
          queryKey="addresses"
          crud={api.employees.addresses}
          columns={ADDRESS_COLUMNS}
          fields={ADDRESS_FIELDS}
          entityLabel="Adresse"
          createLabel="Neue Adresse"
          emptyText="Keine Adressen hinterlegt."
          canWrite={canWrite}
          toValues={(row) => ({
            street: row?.street ?? "",
            houseNumber: row?.houseNumber ?? "",
            postalCode: row?.postalCode ?? "",
            city: row?.city ?? "",
            residenceType: row?.residenceType ?? "ERSTER_WOHNSITZ",
            note: row?.note ?? "",
          })}
          fromValues={(values) => ({
            street: str(values, "street"),
            houseNumber: str(values, "houseNumber") || null,
            postalCode: str(values, "postalCode"),
            city: str(values, "city"),
            residenceType: str(values, "residenceType") as WohnsitzArt,
            note: str(values, "note") || null,
          })}
        />
      </section>
    </div>
  )
}
